import React, { useState, useEffect } from "react";
import {
  FiX,
  FiSearch,
  FiClock,
  FiAlertTriangle,
  FiCheckCircle,
  FiRefreshCw,
  FiDollarSign,
  FiUsers,
  FiEdit2,
  FiSlash,
  FiInfo,
} from "react-icons/fi";
import { format } from "date-fns";
import SkeletonTable from "./common/SkeletonTable";
import { usePaginatedFetch } from "../hooks/usePaginatedFetch";
import { userRequest } from "../utils/requestMethods";
import { formatCurrency } from "../utils/currencyUtils";
import { toast } from "react-toastify";

const REASON_BADGES = {
  late: {
    label: "Late Clock-In",
    color: "bg-amber-50 text-amber-800 border-amber-200",
    icon: FiClock,
  },
  absent: {
    label: "Absent (No Clock-In)",
    color: "bg-rose-50 text-rose-800 border-rose-200",
    icon: FiSlash,
  },
};

const StaffLatenessPenaltiesModal = ({ isOpen, onClose, school, refreshSchool }) => {
  const schoolId = school?._id;

  // Filters state
  const [includeDeducted, setIncludeDeducted] = useState(false);
  const [selectedReason, setSelectedReason] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [summary, setSummary] = useState([]);

  // Waiver sub-modal state
  const [waiverTarget, setWaiverTarget] = useState(null);
  const [waiverAmount, setWaiverAmount] = useState("");
  const [waiverReason, setWaiverReason] = useState("");
  const [savingWaiver, setSavingWaiver] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    items: penalties,
    loading,
    error,
    page,
    setPage,
    limit,
    setLimit,
    totalDocuments,
    totalPages,
    refetch: fetchPenalties,
  } = usePaginatedFetch(schoolId ? `/staff-attendance/${schoolId}/lateness-penalties` : null, {
    enabled: Boolean(isOpen && schoolId),
    initialLimit: 20,
    params: {
      includeDeducted: includeDeducted ? "true" : undefined,
    },
    deps: [includeDeducted],
    mapResponse: (data) => {
      if (Array.isArray(data?.summary)) {
        setSummary(data.summary);
      }
      return {
        items: data?.penalties || [],
        totalDocuments: data?.total_documents || 0,
        totalPages: data?.total_pages || 1,
      };
    },
  });

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [includeDeducted, selectedReason, debouncedSearch, setPage]);

  // Client-side filtering for reason and search
  const filteredPenalties = penalties.filter((row) => {
    if (selectedReason !== "all" && row.reason !== selectedReason) {
      return false;
    }
    if (!debouncedSearch.trim()) return true;
    const term = debouncedSearch.toLowerCase();
    const staffName = `${row.staff?.firstname || ""} ${row.staff?.lastname || ""}`
      .trim()
      .toLowerCase();
    const staffEmail = (row.staff?.email || "").toLowerCase();
    const waiverReasonText = (row.waiver_reason || "").toLowerCase();
    return staffName.includes(term) || staffEmail.includes(term) || waiverReasonText.includes(term);
  });

  // Aggregated summary numbers from backend summary array
  const totalNetDue = summary.reduce((sum, s) => sum + (s.net_due || 0), 0);
  const totalWaived = summary.reduce((sum, s) => sum + (s.total_waived || 0), 0);
  const totalStaffCount = summary.length;
  const totalPenaltiesCount = summary.reduce((sum, s) => sum + (s.count || 0), 0);

  // Open waiver modal
  const openWaiverModal = (penalty) => {
    setWaiverTarget(penalty);
    setWaiverAmount(
      penalty.waived_amount !== undefined && penalty.waived_amount !== null
        ? penalty.waived_amount
        : 0,
    );
    setWaiverReason(penalty.waiver_reason || "");
  };

  const closeWaiverModal = () => {
    if (savingWaiver) return;
    setWaiverTarget(null);
    setWaiverAmount("");
    setWaiverReason("");
  };

  const handleSaveWaiver = async () => {
    if (!waiverTarget) return;

    const amountNum = Number(waiverAmount);
    if (isNaN(amountNum) || amountNum < 0) {
      toast.error("Waiver amount must be a positive number or zero");
      return;
    }
    if (amountNum > waiverTarget.amount_snapshot) {
      toast.error(
        `Waiver amount cannot exceed original penalty amount of ${formatCurrency(waiverTarget.amount_snapshot)}`,
      );
      return;
    }

    try {
      setSavingWaiver(true);
      await userRequest.post(
        `/staff-attendance/${schoolId}/lateness-penalties/${waiverTarget._id}/waive`,
        {
          waivedAmount: amountNum,
          reason: waiverReason.trim() || undefined,
        },
      );

      toast.success(
        amountNum > 0
          ? `Waiver of ${formatCurrency(amountNum)} recorded successfully`
          : "Waiver cleared successfully",
      );

      closeWaiverModal();
      await fetchPenalties();
      if (refreshSchool) {
        refreshSchool();
      }
    } catch (err) {
      console.error("Error saving penalty waiver:", err);
      toast.error(err.response?.data?.message || "Failed to record waiver");
    } finally {
      setSavingWaiver(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-6 animate-fadeIn">
      <div className="bg-white rounded-3xl w-full max-w-5xl h-[90vh] max-h-[850px] flex flex-col shadow-2xl overflow-hidden border border-slate-100">
        {/* Modal Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-slate-800">
                Staff Lateness & Absence Penalties
              </h2>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                {totalDocuments} {totalDocuments === 1 ? "Record" : "Records"}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Review accumulated lateness penalties, grant administrative waivers, and track payroll
              deductions
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors"
            title="Close modal"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Summary Stat Cards */}
        <div className="px-6 py-3.5 bg-slate-50/80 border-b border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white p-3 rounded-2xl border border-slate-200/80 shadow-xs">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <FiDollarSign className="w-3.5 h-3.5 text-indigo-600" />
              <span>Outstanding Net Due</span>
            </div>
            <p className="text-base sm:text-lg font-bold text-indigo-900 mt-1">
              {formatCurrency(totalNetDue)}
            </p>
          </div>

          <div className="bg-white p-3 rounded-2xl border border-slate-200/80 shadow-xs">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <FiUsers className="w-3.5 h-3.5 text-blue-600" />
              <span>Staff Members</span>
            </div>
            <p className="text-base sm:text-lg font-bold text-slate-800 mt-1">{totalStaffCount}</p>
          </div>

          <div className="bg-white p-3 rounded-2xl border border-slate-200/80 shadow-xs">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <FiCheckCircle className="w-3.5 h-3.5 text-emerald-600" />
              <span>Total Waived</span>
            </div>
            <p className="text-base sm:text-lg font-bold text-emerald-700 mt-1">
              {formatCurrency(totalWaived)}
            </p>
          </div>

          <div className="bg-white p-3 rounded-2xl border border-slate-200/80 shadow-xs">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <FiClock className="w-3.5 h-3.5 text-amber-600" />
              <span>Total Penalties</span>
            </div>
            <p className="text-base sm:text-lg font-bold text-slate-800 mt-1">
              {totalPenaltiesCount || totalDocuments}
            </p>
          </div>
        </div>

        {/* Toolbar & Filters */}
        <div className="px-6 py-3.5 bg-white border-b border-slate-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap flex-1">
            <div className="relative flex-1 sm:w-52 min-w-[150px]">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
              <input
                type="text"
                placeholder="Search staff name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
              />
            </div>

            <select
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
              className="text-xs py-1.5 px-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 font-medium cursor-pointer"
            >
              <option value="all">All Reasons (Late & Absent)</option>
              <option value="late">Late Clock-In Only</option>
              <option value="absent">Absent Only</option>
            </select>

            <label className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 cursor-pointer select-none bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200 hover:bg-slate-100 transition-colors">
              <input
                type="checkbox"
                checked={includeDeducted}
                onChange={(e) => setIncludeDeducted(e.target.checked)}
                className="rounded text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5 cursor-pointer"
              />
              <span>Include Deducted</span>
            </label>

            <button
              onClick={fetchPenalties}
              disabled={loading}
              className="p-2 text-slate-500 hover:text-indigo-600 rounded-xl hover:bg-slate-100 transition-colors disabled:opacity-50 flex-shrink-0"
              title="Refresh penalties"
            >
              <FiRefreshCw
                className={`w-3.5 h-3.5 ${loading ? "animate-spin text-indigo-600" : ""}`}
              />
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="px-6 py-2.5 bg-rose-50 border-b border-rose-100 flex items-center gap-2 text-xs font-semibold text-rose-700">
            <FiAlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Table Content */}
        <div className="flex-1 overflow-y-auto">
          {loading && !filteredPenalties.length ? (
            <div className="p-8">
              <SkeletonTable rows={6} columns={7} />
            </div>
          ) : !filteredPenalties.length ? (
            <div className="flex flex-col items-center justify-center h-64 text-center p-6">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-3">
                <FiClock className="w-6 h-6" />
              </div>
              <p className="text-sm font-semibold text-slate-700">No lateness penalties found</p>
              <p className="text-xs text-slate-400 mt-1 max-w-xs">
                {!includeDeducted
                  ? "Great news! There are no outstanding penalties awaiting deduction for this period."
                  : "No matching penalty records found for the selected filters."}
              </p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-slate-100 bg-slate-50/90 backdrop-blur text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  <th className="py-3 pl-6 pr-3">Staff Member</th>
                  <th className="py-3 px-3">Date & Type</th>
                  <th className="py-3 px-3">Timing</th>
                  <th className="py-3 px-3">Original</th>
                  <th className="py-3 px-3">Waived</th>
                  <th className="py-3 px-3">Net Due</th>
                  <th className="py-3 px-3">Status</th>
                  <th className="py-3 pr-6 pl-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {filteredPenalties.map((penalty) => {
                  const reasonMeta = REASON_BADGES[penalty.reason] || {
                    label: penalty.reason || "Late",
                    color: "bg-slate-100 text-slate-700 border-slate-200",
                    icon: FiClock,
                  };

                  const staffName = penalty.staff
                    ? `${penalty.staff.firstname || ""} ${penalty.staff.lastname || ""}`.trim() ||
                      penalty.staff.name ||
                      "Unknown Staff"
                    : "Unknown Staff";
                  const staffEmail = penalty.staff?.email || "";
                  const initials = staffName
                    .split(" ")
                    .filter(Boolean)
                    .map((n) => n[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase();

                  const originalAmount = penalty.amount_snapshot || 0;
                  const waivedAmount = penalty.waived_amount || 0;
                  const netDue = Math.max(0, originalAmount - waivedAmount);

                  const scheduledHour = penalty.scheduled_time_snapshot?.hour;
                  const scheduledMinute = penalty.scheduled_time_snapshot?.minute;
                  const scheduledTimeStr =
                    scheduledHour !== undefined && scheduledMinute !== undefined
                      ? `${String(scheduledHour).padStart(2, "0")}:${String(scheduledMinute).padStart(2, "0")}`
                      : null;

                  return (
                    <tr key={penalty._id} className="hover:bg-slate-50/80 transition-colors">
                      {/* Staff Member */}
                      <td className="py-3 pl-6 pr-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                            {penalty.staff?.avatar ? (
                              <img
                                src={penalty.staff.avatar}
                                alt={staffName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="font-semibold text-slate-500 text-xs">
                                {initials || "ST"}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">{staffName}</p>
                            {staffEmail && (
                              <p className="text-[11px] text-slate-400">{staffEmail}</p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Date & Type */}
                      <td className="py-3 px-3">
                        <div className="space-y-1">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full border ${reasonMeta.color}`}
                          >
                            <reasonMeta.icon className="w-3 h-3 shrink-0" />
                            <span>{reasonMeta.label}</span>
                          </span>
                          <p className="text-[11px] text-slate-500">
                            {penalty.clock_in_date
                              ? format(new Date(penalty.clock_in_date), "dd MMM yyyy")
                              : "—"}
                          </p>
                        </div>
                      </td>

                      {/* Timing */}
                      <td className="py-3 px-3">
                        {penalty.reason === "absent" ? (
                          <span className="text-[11px] text-slate-400 italic">No clock-in</span>
                        ) : (
                          <div className="space-y-0.5 text-[11px]">
                            {penalty.clock_in_time && (
                              <p className="text-slate-700 font-medium">
                                Clocked:{" "}
                                <span className="font-semibold text-rose-700">
                                  {format(new Date(penalty.clock_in_time), "hh:mm a")}
                                </span>
                              </p>
                            )}
                            {scheduledTimeStr && (
                              <p className="text-slate-400">Cutoff: {scheduledTimeStr}</p>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Original Amount */}
                      <td className="py-3 px-3 text-slate-600 font-medium">
                        {formatCurrency(originalAmount)}
                      </td>

                      {/* Waived Amount */}
                      <td className="py-3 px-3">
                        {waivedAmount > 0 ? (
                          <div>
                            <span className="font-semibold text-emerald-700">
                              -{formatCurrency(waivedAmount)}
                            </span>
                            {penalty.waiver_reason && (
                              <p
                                className="text-[10px] text-slate-400 truncate max-w-[120px]"
                                title={penalty.waiver_reason}
                              >
                                "{penalty.waiver_reason}"
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>

                      {/* Net Due */}
                      <td className="py-3 px-3">
                        <span
                          className={`font-bold ${
                            netDue > 0 ? "text-indigo-900" : "text-emerald-700"
                          }`}
                        >
                          {formatCurrency(netDue)}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="py-3 px-3">
                        {penalty.deducted ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                            Deducted in Payroll
                          </span>
                        ) : waivedAmount >= originalAmount && originalAmount > 0 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                            Fully Waived
                          </span>
                        ) : waivedAmount > 0 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                            Partially Waived
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                            Outstanding
                          </span>
                        )}
                      </td>

                      {/* Action */}
                      <td className="py-3 pr-6 pl-3 text-right">
                        {penalty.deducted ? (
                          <span className="text-xs text-slate-400 font-medium">Settled</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openWaiverModal(penalty)}
                            className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full border border-indigo-200 text-indigo-700 bg-indigo-50/60 hover:bg-indigo-100 transition-colors shadow-xs"
                          >
                            <FiEdit2 className="w-3 h-3" />
                            <span>{waivedAmount > 0 ? "Edit Waiver" : "Waive"}</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer & Pagination */}
        <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div className="flex items-center gap-3">
            <span>
              Showing{" "}
              <span className="font-semibold text-slate-700">{filteredPenalties.length}</span> of{" "}
              <span className="font-semibold text-slate-700">{totalDocuments}</span> records
            </span>
            <div className="flex items-center gap-1 text-[11px] text-slate-500">
              <span>Per page:</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="py-0.5 px-2 bg-white border border-slate-200 rounded-lg text-slate-700 font-medium focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={page <= 1 || loading}
              className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-slate-700 transition-colors shadow-sm"
            >
              Previous
            </button>
            <span className="px-2 font-medium">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              disabled={page >= totalPages || loading}
              className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-slate-700 transition-colors shadow-sm"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Waiver Edit/Apply Sub-Modal */}
      {waiverTarget && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-60 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden border border-slate-100 flex flex-col">
            <div className="px-5 py-4 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center">
                  <FiDollarSign className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800">
                    {waiverTarget.waived_amount > 0 ? "Edit Penalty Waiver" : "Waive Penalty"}
                  </h3>
                  <p className="text-xs text-slate-500">
                    {waiverTarget.staff
                      ? `${waiverTarget.staff.firstname || ""} ${waiverTarget.staff.lastname || ""}`.trim()
                      : "Staff Member"}
                  </p>
                </div>
              </div>
              <button
                onClick={closeWaiverModal}
                disabled={savingWaiver}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4 text-xs">
              {/* Penalty Context Summary */}
              <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/80 space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-slate-500">Date:</span>
                  <span className="font-semibold text-slate-700">
                    {waiverTarget.clock_in_date
                      ? format(new Date(waiverTarget.clock_in_date), "dd MMMM yyyy")
                      : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Reason:</span>
                  <span className="font-semibold text-slate-700 capitalize">
                    {waiverTarget.reason === "late" ? "Late Clock-in" : "Absent (No Clock-in)"}
                  </span>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-1.5">
                  <span className="text-slate-500">Original Penalty Snapshot:</span>
                  <span className="font-bold text-slate-900">
                    {formatCurrency(waiverTarget.amount_snapshot)}
                  </span>
                </div>
              </div>

              {/* Amount to waive input */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Amount to Waive (₦)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold text-xs">
                    ₦
                  </span>
                  <input
                    type="number"
                    min="0"
                    max={waiverTarget.amount_snapshot}
                    step="50"
                    value={waiverAmount}
                    onChange={(e) => setWaiverAmount(e.target.value)}
                    placeholder="0"
                    className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-semibold"
                  />
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setWaiverAmount(waiverTarget.amount_snapshot)}
                    className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 transition-colors"
                  >
                    Waive Full ({formatCurrency(waiverTarget.amount_snapshot)})
                  </button>
                  <button
                    type="button"
                    onClick={() => setWaiverAmount(0)}
                    className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 transition-colors"
                  >
                    Reset (₦0)
                  </button>
                </div>
              </div>

              {/* Net deduction preview */}
              <div className="bg-indigo-50/60 border border-indigo-100 rounded-xl p-3 flex justify-between items-center text-xs">
                <span className="text-indigo-900 font-medium">Net Payroll Deduction:</span>
                <span className="text-sm font-bold text-indigo-900">
                  {formatCurrency(
                    Math.max(0, (waiverTarget.amount_snapshot || 0) - (Number(waiverAmount) || 0)),
                  )}
                </span>
              </div>

              {/* Reason textarea */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Waiver Reason (Optional note)
                </label>
                <textarea
                  rows={2}
                  value={waiverReason}
                  onChange={(e) => setWaiverReason(e.target.value)}
                  placeholder="e.g. First offense warning, approved excuse, public transport disruption"
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs"
                />
              </div>

              <div className="flex items-start gap-2 text-[11px] text-slate-500 bg-slate-50 p-2.5 rounded-lg">
                <FiInfo className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                <span>
                  Waivers can be modified until the penalty is settled in a finalized payroll batch.
                </span>
              </div>
            </div>

            {/* Sub-modal footer */}
            <div className="px-5 py-3.5 bg-slate-50 border-t border-slate-100 flex gap-2.5 justify-end">
              <button
                type="button"
                disabled={savingWaiver}
                onClick={closeWaiverModal}
                className="px-4 py-2 rounded-full border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 font-semibold text-xs transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={savingWaiver}
                onClick={handleSaveWaiver}
                className="inline-flex items-center px-5 py-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs transition-colors disabled:opacity-50 shadow-sm"
              >
                {savingWaiver ? "Saving Waiver..." : "Save Waiver"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffLatenessPenaltiesModal;
